import { useStore } from '@nanostores/react';
import { motion } from 'framer-motion';
import { memo, useMemo } from 'react';
import type { ScrollToBottom } from 'use-stick-to-bottom';
import { AssistantMessageContent } from './AssistantMessageContent';
import { AssistantMessageDetailsDialog } from './AssistantMessageDetailsDialog';
import { SearchSource } from './SearchSource';
import { AssistantMessageProvider } from './context';
import { useProcessedParts, useQuickActions, useTodoGroups } from './hooks';
import { Thinking } from '~/components/chat/Thinking';
import { AssistantMessageButtons } from '~/components/chat/assistant-message/AssistantMessageButtons';
import { shouldHidePart } from '~/components/chat/assistant-message/utils/should-hide-part';
import { VersionSnapshotCard } from '~/components/chat/version-history';
import { trackVersionHistoryRestoreModalOpened } from '~/lib/analytics/version-history';
import { versionHistoryStore } from '~/lib/stores/version-history/version-history';
import type { AssistantMessage as AssistantMessageType, OtherMessage } from '~/types/chat';

interface AssistantMessageProps {
  message: AssistantMessageType | OtherMessage;
  disableQuickActions?: boolean;
  scrollToBottom: ScrollToBottom;
  useDynamicHeight?: boolean;
}

export const AssistantMessage = memo((props: AssistantMessageProps) => {
  const { message, disableQuickActions, scrollToBottom, useDynamicHeight } = props;

  const snapshots = useStore(versionHistoryStore.snapshots);
  const snapshot = message ? snapshots.find((item) => item.messageId === message.id) : undefined;
  const versionNumber = snapshots.length - snapshots.findIndex((s) => s.id === snapshot?.id);
  const isCurrentVersion = snapshot?.id === snapshots.at(0)?.id;

  const sources = message.parts?.filter((part) => part.type === 'source') ?? [];

  const { markdownContent, quickActions } = useQuickActions(message.content, disableQuickActions, scrollToBottom);

  const processedParts = useProcessedParts(message.parts);
  const todoGroupsData = useTodoGroups(processedParts);

  const visibleParts = useMemo(() => {
    return processedParts.filter((part, index) => !shouldHidePart(part, index, todoGroupsData));
  }, [processedParts, todoGroupsData]);

  const dynamicHeight = `calc(
    100vh
    - var(--header-height)
    - var(--computed-last-user-message-height)
    - var(--computed-prompt-box-height)
    - var(--chat-messages-gap)
    - var(--chat-padding)
    - var(--workbench-top-offset)
  )`;

  return (
    <AssistantMessageProvider>
      <div className="grid [&>*]:[grid-area:1/1/2/2]">
        <motion.div
          className="pointer-events-none"
          initial={{
            height: message.id === 'mock-assistant-message' ? 'auto' : useDynamicHeight ? dynamicHeight : 'auto',
          }}
          animate={{
            height: useDynamicHeight ? dynamicHeight : 'auto',
          }}
          transition={{ duration: 0.375, ease: 'easeOut' }}
        />

        <div className="flex flex-col gap-3 overflow-hidden w-full">
          <div className="flex items-center min-h-6.5 select-none justify-between">
            <i className="i-bolt:logos-bolt?mask w-[40px] -translate-x-3px" />
            <AssistantMessageButtons message={message} />
          </div>

          {sources.length > 0 && (
            <div>
              <small className="font-semibold inline-block mb-1">Sources</small>
              <div className="flex flex-wrap gap-2">
                {sources.map((part, index) => (
                  <SearchSource key={part.source.id} index={index} source={part.source} />
                ))}
              </div>
            </div>
          )}

          <Thinking
            annotations={message.annotations}
            parts={message.parts}
            content={message.content}
            lastVisiblePart={visibleParts.at(-1)}
          />

          <AssistantMessageContent
            markdownContent={markdownContent}
            scrollToBottom={scrollToBottom}
            message={message}
            processedParts={processedParts}
            todoGroupsData={todoGroupsData}
            visibleParts={visibleParts}
          />

          <AssistantMessageDetailsDialog message={message} />

          {quickActions.length > 0 && <div className="flex items-center gap-2 flex-wrap">{quickActions}</div>}
          {snapshot && (
            <VersionSnapshotCard
              snapshot={snapshot}
              versionNumber={versionNumber}
              isCurrentVersion={isCurrentVersion}
              handleRollback={() => {
                const numberOfVersionsRollingBack = snapshots.length - versionNumber;
                trackVersionHistoryRestoreModalOpened(snapshot.messageId, numberOfVersionsRollingBack);
                versionHistoryStore.restoreConfirmationDialog.open(snapshot);
              }}
              handlePreview={() => {
                versionHistoryStore.previewSnapshot(snapshot, 'chat');
              }}
            />
          )}
        </div>
      </div>
    </AssistantMessageProvider>
  );
});

AssistantMessage.displayName = 'AssistantMessage';
